import * as React from 'react';
import classnames from 'classnames';

import DeliverableHeader from '../header/DeliverableHeader';
import DeliverableTabInformation from '../DeliverableTabInformation';
import DeliverableTabMedia from '../DeliverableTabMedia';
import DeliverableTabWrapper from '../deliverable-tab-wrapper/deliverable-tab-wrapper';

import { DELIVERABLE_TAB_INFORMATION, DELIVERABLE_TAB_MEDIA } from 'utils/deliverables';
import { getEntityFromProfile } from 'utils/view';
import { expandDataProps } from 'utils/helper/common';
import { isInfluencerProfileRejected } from 'utils/profile';

import type {
  Data,
  Deliverable as DeliverableAlias,
  DeliverableTabKey,
  DeliverableUploadedEventAction,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';

import type { SyntheticEvent } from 'react';

type Props = {
  selectedTab?: 'information' | 'media',
  availableChannels?: Array<string>,
  deliverable: DeliverableAlias,
  campaignId: number,
  subcampaign: Subcampaign,
  hideInfoNote?: boolean,
  hasChanges?: boolean,
  disabled?: boolean,
  offerId: number,
  isForBrand: boolean,
  influencerProfile?: SubcampaignInfluencerProfile,
  isExpanded: boolean,
  data?: Data,
  onRemove?: (deliverable: DeliverableAlias) => void,
  onEdit?: (deliverable: DeliverableAlias) => void,
  onDuplicate?: (deliverable: DeliverableAlias) => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void
};

type State = {
  selectedTab: DeliverableTabKey,
  inEditMode: boolean,
  inApprovalMode: boolean
};

class Deliverable extends React.Component<Props, State> {
  state = {
    selectedTab: this.props.selectedTab || DELIVERABLE_TAB_INFORMATION,
    inEditMode: false,
    inApprovalMode: false
  };

  deliverablesContainerRef: React.RefObject<any> = React.createRef();

  handleApprovalMode = (): void => {
    this.setState(state => ({
      inApprovalMode: !state.inApprovalMode
    }));
  };

  handleEdit = (value: any) => {
    const { onEdit } = this.props;

    if (onEdit) {
      onEdit(value);
    }

    this.setState({ inEditMode: false });
  };

  handleEnterEditMode = () => {
    this.setState({ inEditMode: true });
  };

  handleTabChanged = (event: SyntheticEvent<HTMLDivElement | HTMLButtonElement>): void => {
    const nextTab = event.currentTarget.dataset.tab;

    if (nextTab === 'information' || nextTab === 'media') {
      this.setState({
        selectedTab: nextTab,
        inApprovalMode: false
      });
    }
  };

  renderTabContent(): React.ReactNode {
    const {
      campaignId,
      deliverable,
      subcampaign,
      offerId,
      isForBrand,
      onTrackDeliverableUploadEvent
    } = this.props;

    const { selectedTab } = this.state;
    const isViewForRejectedInfluencerProfile = isInfluencerProfileRejected(subcampaign.creatorProfile);
    const showOverlay = isViewForRejectedInfluencerProfile || subcampaign.state === 'revoked';

    // always render all tabs and hide them with CSS in order not to lose internal UI state
    return (
      <React.Fragment>
        <DeliverableTabWrapper
          key={`tab-${DELIVERABLE_TAB_INFORMATION}`}
          isVisible={!selectedTab || selectedTab === DELIVERABLE_TAB_INFORMATION}
        >
          <DeliverableTabInformation
            deliverable={deliverable}
          />
        </DeliverableTabWrapper>

        <DeliverableTabWrapper
          key={`tab-${DELIVERABLE_TAB_MEDIA}`}
          isVisible={selectedTab === DELIVERABLE_TAB_MEDIA}
        >
          <div className="deliverable__tab-overlay-container">
            <DeliverableTabMedia
              deliverablesContainerRef={this.deliverablesContainerRef}
              isForBrand={isForBrand}
              canModify={subcampaign.offer.id === offerId} // deliverable from the accepted offer
              campaignId={campaignId}
              subcampaign={subcampaign}
              deliverable={deliverable}
              onApprovalMode={this.handleApprovalMode}
              onTrackDeliverableUploadEvent={onTrackDeliverableUploadEvent}
            />
            {showOverlay && <div className="deliverable__tab-overlay" />}
          </div>
        </DeliverableTabWrapper>
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const {
      hasChanges,
      offerId,
      subcampaign,
      deliverable,
      disabled,
      isForBrand,
      influencerProfile,
      isExpanded,
      data
    } = this.props;

    const { selectedTab, inEditMode, inApprovalMode } = this.state;

    const entity = influencerProfile ? getEntityFromProfile(influencerProfile) : null;

    const deliverableClasses = classnames('deliverable', {
      'deliverable--wide': entity,
      'deliverable--disabled': disabled,
      'deliverable--mode-inline-edit': inEditMode
    });

    const bodyClasses = classnames('deliverable__body', {
      'deliverable__body--wide': entity && !inApprovalMode
    });

    const contentClasses = classnames('deliverable__content', {
      'deliverable__content--hidden': inApprovalMode
    });

    return (
      <section
        className={deliverableClasses}
        {...expandDataProps(data)}
      >
        <div className="deliverable__header">
          <DeliverableHeader
            hasChanges={hasChanges}
            offerId={offerId}
            subcampaign={subcampaign}
            deliverable={deliverable}
            influencerProfile={entity && influencerProfile}
            selectedTab={selectedTab}
            isForBrand={isForBrand}
            isDisabled={inApprovalMode}
            hideTabs={!isExpanded}
            onTabChanged={this.handleTabChanged}
          />
        </div>
        {
          isExpanded && (
            <div
              ref={this.deliverablesContainerRef}
              className={bodyClasses}
            >
              <div className={contentClasses}>
                {this.renderTabContent()}
              </div>
            </div>
          )
        }
      </section>
    );
  }
}

export default Deliverable;
